import Decimal from 'decimal.js';
import pluralize from 'pluralize';
import { utils } from '~/utils';
import { preapprovalPeriodWordMap, recurrencePeriodWordMap } from '~/utils/billing-period-text';
import { DiscountModel, DiscountType } from '../DiscountModel';
import { RoundPrices } from '../PriceIncreaseModel';
import * as serviceBillingModel from '../service-billing/trigger-predicates';
import { ServiceCostType } from './model';
import { isFixedPrice, isRangePrice, isVariablePrice, price } from './pricing';
export const costDisplayTextsPath = 'service.model.costDisplay';
export function minPrice(cost) {
    return cost.pricingType === ServiceCostType.Range ? cost.minPrice : null;
}
export function finalPrice(cost, quantity) {
    if (isRangePrice(cost)) {
        return utils.applyDiscount(cost.maxPrice, cost.discount);
    } else if (isFixedPrice(cost)) {
        return utils.applyDiscount(cost.price, cost.discount);
    } else {
        return utils.calculateFinalPrice(cost.price, cost.discount, quantity);
    }
}
export function minPriceFinal(cost) {
    return cost.pricingType === ServiceCostType.Range && cost.minPrice ? utils.applyDiscount(cost.minPrice, cost.discount) : null;
}
export function discount(cost) {
    return cost.discount;
}
export function expiredDiscount(cost) {
    return cost.expiredDiscount;
}
export function discountPeriodExpiration(cost) {
    return cost.discount.expiration();
}
export function unitCap(cost) {
    return isVariablePrice(cost) ? cost.unitCap : null;
}
export function preapprovedControlledByAdvancedSettings(cost) {
    return isVariablePrice(cost) && (cost.preapprovedControlledByAdvancedSettings || false);
}
export function unitName(cost) {
    if (cost.pricingType === ServiceCostType.Fixed || cost.pricingType === ServiceCostType.Range) {
        return null;
    } else {
        return cost.unitName;
    }
}
export function priceIncrease(cost) {
    return cost.priceIncrease;
}
export function nextYearPrice(cost) {
    var _cost_discount_expiration, _cost_discount;
    if (cost.priceIncrease.isNonPositiveIncrease()) {
        return null;
    }
    const basePrice = ((_cost_discount = cost.discount) === null || _cost_discount === void 0 ? void 0 : (_cost_discount_expiration = _cost_discount.expiration()) === null || _cost_discount_expiration === void 0 ? void 0 : _cost_discount_expiration.hasExpiration()) ? price(cost) : finalPrice(cost, 1);
    const rawResult = basePrice.times(cost.priceIncrease.percentIncrease().plus(100)).div(100);
    switch(cost.priceIncrease.roundPrices()){
        case RoundPrices.None:
            return rawResult.toDP(2).toNumber();
        case RoundPrices.Down1:
            return rawResult.floor().toNumber();
        case RoundPrices.Down5:
            return rawResult.minus(rawResult.mod(5)).toNumber();
        case RoundPrices.Down10:
            {
                return rawResult.minus(rawResult.mod(10)).toNumber();
            }
    }
}
export function formattedUnitName(cost, costDisplayTexts) {
    const un = unitName(cost);
    return un || costDisplayTexts.defaultUnit();
}
export function pluralizedUnitName(cost, count, costDisplayTexts) {
    const userInput = unitName(cost);
    if (userInput) return pluralize(userInput, count);
    return costDisplayTexts.defaultUnitPlural({
        count
    });
}
export function formattedUnitNameShort(cost, costDisplayTexts) {
    const un = unitName(cost);
    return un || costDisplayTexts.defaultUnitShort();
}
function preapprovedPeriodWord(billing, periodTexts) {
    if (!billing) return null;
    if (serviceBillingModel.isOnDemand(billing)) {
        return preapprovalPeriodWordMap(periodTexts)[billing.preapprovalPeriod] || null;
    }
    if (serviceBillingModel.isRecurring(billing)) {
        return recurrencePeriodWordMap(periodTexts)[billing.recurrencePeriod] || null;
    }
    return null;
}
export function stringifyServiceCost(cost, billing, periodTexts, costDisplayTexts) {
    var _ref, _ref1;
    const applyDiscount = function applyDiscount(price) {
        let discount = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : new Decimal(0), discountType = arguments.length > 2 && arguments[2] !== void 0 ? arguments[2] : DiscountType.Value;
        const discountedPrice = utils.applyDiscount(price, DiscountModel.make(discountType, discount));
        return utils.formatDollarAmount(discountedPrice);
    };
    const { discount, pricingType } = cost;
    const discountAmount = (_ref = discount === null || discount === void 0 ? void 0 : discount.amount()) !== null && _ref !== void 0 ? _ref : new Decimal(0);
    const discountType = (_ref1 = discount === null || discount === void 0 ? void 0 : discount.type()) !== null && _ref1 !== void 0 ? _ref1 : DiscountType.Value;
    switch(pricingType){
        case ServiceCostType.Fixed:
            return {
                costDisplay: applyDiscount(cost.price, discountAmount, discountType)
            };
        case ServiceCostType.Variable:
            {
                const unitCap = cost.unitCap;
                const unitName = formattedUnitName(cost, costDisplayTexts);
                const period = preapprovedPeriodWord(billing, periodTexts);
                const unitCapCount = unitCap ? unitCap.toString() : undefined;
                const pluralizedUnit = unitCap ? pluralizedUnitName(cost, unitCap.toNumber(), costDisplayTexts) : unitName;
                const unitCapDisplay = unitCapCount ? period ? costDisplayTexts.preapprovedPerPeriod({
                    count: unitCapCount,
                    unit: pluralizedUnit,
                    period
                }) : costDisplayTexts.preapproved({
                    count: unitCapCount,
                    unit: pluralizedUnit
                }) : undefined;
                return {
                    costDisplay: costDisplayTexts.pricePerUnit({
                        price: applyDiscount(cost.price, discountAmount, discountType),
                        unitName
                    }),
                    unitCapDisplay
                };
            }
        case ServiceCostType.Range:
            {
                if (cost.minPrice) {
                    return {
                        costDisplay: "".concat(applyDiscount(cost.minPrice, discountAmount, discountType), "—").concat(applyDiscount(cost.maxPrice, discountAmount, discountType))
                    };
                } else {
                    return {
                        costDisplay: costDisplayTexts.upTo({
                            price: applyDiscount(cost.maxPrice, discountAmount, discountType)
                        })
                    };
                }
            }
    }
}
export function formattedCost(cost, costDisplayTexts, periodTexts) {
    const { costDisplay, unitCapDisplay } = stringifyServiceCost(cost, undefined, periodTexts, costDisplayTexts);
    return unitCapDisplay ? costDisplayTexts.formattedCostWithCap({
        costDisplay,
        unitCapDisplay
    }) : costDisplay;
}
export function match(cost, visitor) {
    switch(cost.pricingType){
        case ServiceCostType.Fixed:
            return visitor.fixed(cost);
        case ServiceCostType.Variable:
            return visitor.variable(cost);
        case ServiceCostType.Range:
            return visitor.range(cost);
    }
}
